import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCourseApi, CourseDetails } from '../../hooks/useCourseApi';
import { Curriculum } from './Curriculum';
import { checkEnrollment } from '../../services/enrollmentService';
import { BASE_URL } from '../../apiConfig';
import { cardTheme } from '../../styles/cardTheme';
import {
  emptyCodeEnrollResult,
  mapCourseEnrollByCodeResponse,
  type CourseEnrollUiResult,
} from '../../utils/courseEnrollmentLabels';
import { getCoursePath, resolveCourseIdFromParam } from '../../utils/courseSlug';
import CourseEnrollResultPopup from './CourseEnrollResultPopup';
import SEOHead from '../common/SEOHead';
import {
  BRAND_FULL_NAME,
  BRAND_NAME,
  SITE_URL,
} from '../../config/brand';
import {
  BookOpen,
  Play,
  CheckCircle,
  GraduationCap,
  Video,
  Target,
  ShoppingCart,
  CalendarDays,
  Sparkles,
  Loader2,
} from 'lucide-react';

const getGradeInArabic = (grade?: string | null): string => {
  if (!grade || grade === 'null' || grade === 'undefined') return '';
  const gradeTranslations: Record<string, string> = {
    Secondary1: 'أولى ثانوي',
    Secondary2: 'ثانية ثانوي',
    Secondary3: 'ثالثة ثانوي',
    secondary1: 'أولى ثانوي',
    secondary2: 'ثانية ثانوي',
    secondary3: 'ثالثة ثانوي',
  };
  return gradeTranslations[grade] || grade;
};

const getTermInArabic = (term?: string | null): string => {
  if (!term || term === 'null' || term === 'undefined') return '';
  const terms: Record<string, string> = {
    First: 'الفصل الأول',
    Second: 'الفصل الثاني',
    Summer: 'الفصل الصيفي',
  };
  return terms[term] || term;
};

const CourseDetail: React.FC = () => {
  const { courseSlug } = useParams<{ courseSlug: string }>();
  const id = resolveCourseIdFromParam(courseSlug) || '';
  const { isAuthenticated, currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { fetchCourseDetailsWithoutProgress, isLoading, error, enrollInCourse } = useCourseApi();

  const getCourseImageSrc = (course: CourseDetails) => {
    if (!course.imagePath) return '/default-course.jpg';
    if (course.imagePath.startsWith('http')) return course.imagePath;
    let imagePath = course.imagePath.startsWith('/') ? course.imagePath.slice(1) : course.imagePath;
    const baseUrl = BASE_URL.endsWith('/') ? BASE_URL : `${BASE_URL}/`;
    return `${baseUrl}${imagePath}`;
  };

  const [selectedSection, setSelectedSection] = useState('curriculum');
  const [courseDetails, setCourseDetails] = useState<CourseDetails | null>(null);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [isCheckingEnrollment, setIsCheckingEnrollment] = useState(true);
  const [showCodeInput] = useState(true);
  const [code, setCode] = useState('');
  const [enrollmentError, setEnrollmentError] = useState('');
  const [enrollmentSuccess, setEnrollmentSuccess] = useState(false);
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [enrollResult, setEnrollResult] = useState<CourseEnrollUiResult | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const loadCourseDetails = async () => {
      if (id) {
        const response = await fetchCourseDetailsWithoutProgress(id);
        if (response?.success) setCourseDetails(response.data);
      }
    };
    loadCourseDetails();
  }, [id, fetchCourseDetailsWithoutProgress]);

  // Prefer SEO slug URL; keep UUID-only links working via redirect
  useEffect(() => {
    if (!courseDetails?.id || !courseDetails.courseName) return;
    const canonicalPath = getCoursePath(courseDetails);
    const currentPath = decodeURIComponent(location.pathname);
    if (currentPath !== canonicalPath) {
      navigate(canonicalPath, { replace: true });
    }
  }, [courseDetails, location.pathname, navigate]);

  useEffect(() => {
    const checkUserEnrollment = async () => {
      if (!mounted) {
        setIsCheckingEnrollment(false);
        return;
      }
      if (id && currentUser?.id) {
        setIsCheckingEnrollment(true);
        const enrolled = await checkEnrollment(currentUser.id, id);
        setIsEnrolled(enrolled);
        setIsCheckingEnrollment(false);
      } else {
        setIsCheckingEnrollment(false);
      }
    };
    checkUserEnrollment();
  }, [mounted, id, currentUser?.id]);

  const handleContinue = () => navigate(`/course/${id}/curriculum`);

  const handleCodeSubmit = async () => {
    if (!code.trim()) {
      setEnrollResult(emptyCodeEnrollResult());
      setEnrollmentError('');
      setEnrollmentSuccess(false);
      return;
    }
    if (!isAuthenticated) {
      setEnrollResult(
        mapCourseEnrollByCodeResponse({ success: false, message: 'User not authenticated' })
      );
      setEnrollmentError('');
      setEnrollmentSuccess(false);
      return;
    }
    try {
      setIsEnrolling(true);
      setEnrollmentError('');
      setEnrollmentSuccess(false);
      const response = await enrollInCourse(id || '', code);
      const ui = mapCourseEnrollByCodeResponse(response);
      setEnrollResult(ui);
      if (ui.success) {
        setEnrollmentSuccess(true);
        if (id && currentUser?.id) {
          const enrolled = await checkEnrollment(currentUser.id, id);
          setIsEnrolled(enrolled);
        }
      } else {
        setEnrollmentSuccess(false);
      }
    } catch {
      setEnrollResult(
        mapCourseEnrollByCodeResponse({
          success: false,
          message: 'An error occurred while enrolling in the course',
        })
      );
      setEnrollmentSuccess(false);
    } finally {
      setIsEnrolling(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !isEnrolling) handleCodeSubmit();
  };

  const handleStartCourse = () => {
    setShowSuccessPopup(false);
    setEnrollResult(null);
    navigate(`/course-player/${id}`);
  };

  const handleGoToMyCourses = () => {
    setShowSuccessPopup(false);
    setEnrollResult(null);
    navigate('/student-portal');
  };

  const handleEnrollPrimary = () => {
    if (!enrollResult) return;
    if (enrollResult.success) {
      handleStartCourse();
      return;
    }
    if (enrollResult.kind === 'already_enrolled') {
      handleGoToMyCourses();
      return;
    }
    if (enrollResult.kind === 'not_authenticated') {
      navigate('/login');
      return;
    }
    setEnrollResult(null);
  };

  const handleCheckoutClick = () => {
    const coursePayload = courseDetails
      ? {
          id: courseDetails.id,
          courseName: courseDetails.courseName,
          description: courseDetails.description,
          price: courseDetails.price,
          imagePath: courseDetails.imagePath,
          grade: courseDetails.grade,
          term: courseDetails.term,
        }
      : null;

    if (!isAuthenticated) {
      navigate('/login', {
        state: { redirectTo: '/checkout', course: coursePayload, courseId: courseDetails?.id },
      });
      return;
    }
    if (courseDetails) {
      navigate('/checkout', { state: { course: coursePayload, courseId: courseDetails.id } });
    }
  };

  const tabs = [
    { id: 'curriculum', label: 'المنهج الدراسي', icon: BookOpen },
    { id: 'overview', label: 'نظرة عامة', icon: Target },
  ];

  if (!id) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-gradient-to-b from-green-50/40 via-white to-gold-50/30 px-4">
        <div className={`${cardTheme.shell} max-w-md p-8 text-center`}>
          <p className={`mb-4 text-lg font-bold ${cardTheme.title}`}>تعذّر تحميل الكورس</p>
          <p className="mb-6 text-sm text-gray-600">رابط الكورس غير صحيح</p>
          <button type="button" onClick={() => navigate('/')} className={`rounded-xl px-6 py-2.5 ${cardTheme.btnPrimary}`}>
            العودة للرئيسية
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-gradient-to-b from-green-50/40 via-white to-gold-50/30 px-4">
        <div className="text-center">
          <Loader2 className="mx-auto mb-4 h-10 w-10 animate-spin text-green-700" />
          <p className="text-sm font-medium text-gray-600 sm:text-base">جاري تحميل تفاصيل الكورس...</p>
        </div>
      </div>
    );
  }

  if (error || !courseDetails) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-gradient-to-b from-green-50/40 via-white to-gold-50/30 px-4">
        <div className={`${cardTheme.shell} max-w-md p-8 text-center`}>
          <p className={`mb-4 text-lg font-bold ${cardTheme.title}`}>تعذّر تحميل الكورس</p>
          <p className="mb-6 text-sm text-gray-600">{error || 'الكورس غير متاح حالياً'}</p>
          <button type="button" onClick={() => navigate('/')} className={`rounded-xl px-6 py-2.5 ${cardTheme.btnPrimary}`}>
            العودة للرئيسية
          </button>
        </div>
      </div>
    );
  }

  const totalLessons = courseDetails.units.reduce((total, unit) => total + unit.lessons.length, 0);
  const isFree = courseDetails.price === 0;
  const formattedPrice = courseDetails.price.toLocaleString('ar-EG');
  const courseImage = getCourseImageSrc(courseDetails);
  const courseUrl = getCoursePath(courseDetails);
  const gradeLabel = getGradeInArabic(courseDetails.grade);
  const termLabel = getTermInArabic(courseDetails.term);
  const courseSeoDescription =
    courseDetails.description?.trim() ||
    (gradeLabel
      ? `كورس ${courseDetails.courseName} للصف ${gradeLabel} على منصة أوائل مصر — دروس أونلاين.`
      : `كورس ${courseDetails.courseName} على منصة أوائل مصر — دروس أونلاين.`);
  const seoTitle = gradeLabel
    ? `${courseDetails.courseName} | كورس ${gradeLabel} | ${BRAND_NAME}`
    : `${courseDetails.courseName} | كورس أونلاين | ${BRAND_NAME}`;
  const seoKeywords = [
    courseDetails.courseName,
    gradeLabel ? `كورس ${gradeLabel}` : 'كورس أونلاين',
    BRAND_NAME,
    'كورسات أونلاين',
    'تعليم أونلاين',
  ]
    .filter(Boolean)
    .join(', ');

  const courseStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Course',
    '@id': `${SITE_URL}${courseUrl}#course`,
    name: courseDetails.courseName,
    description: courseSeoDescription,
    url: `${SITE_URL}${courseUrl}`,
    image: courseImage.startsWith('http') ? courseImage : `${SITE_URL}${courseImage}`,
    inLanguage: 'ar',
    provider: {
      '@type': 'EducationalOrganization',
      name: BRAND_FULL_NAME,
      sameAs: SITE_URL,
      url: SITE_URL,
    },
    instructor: {
      '@type': 'Organization',
      name: BRAND_FULL_NAME,
    },
    ...(gradeLabel ? { educationalLevel: gradeLabel } : {}),
    hasCourseInstance: {
      '@type': 'CourseInstance',
      courseMode: 'online',
      courseWorkload: `${totalLessons} درس`,
    },
    offers: {
      '@type': 'Offer',
      category: isFree ? 'Free' : 'Paid',
      price: courseDetails.price,
      priceCurrency: 'EGP',
      availability: 'https://schema.org/InStock',
      url: `${SITE_URL}${courseUrl}`,
      seller: {
        '@type': 'EducationalOrganization',
        name: BRAND_FULL_NAME,
      },
    },
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-gradient-to-b from-green-50/50 via-white to-gold-50/30 font-cairo" dir="rtl">
      <SEOHead
        title={seoTitle}
        description={courseSeoDescription}
        keywords={seoKeywords}
        url={courseUrl}
        image={courseImage}
        type="product"
        breadcrumbs={[
          { name: 'الكورسات', path: `/courses/grade/${String(courseDetails.grade || 'secondary1').toLowerCase()}` },
          { name: courseDetails.courseName, path: courseUrl },
        ]}
        structuredData={courseStructuredData}
      />
      {enrollResult && (
        <CourseEnrollResultPopup
          result={enrollResult}
          onClose={() => setEnrollResult(null)}
          onPrimary={handleEnrollPrimary}
          secondaryLabel={enrollResult.success ? 'كورساتي وحصصي' : undefined}
          onSecondary={enrollResult.success ? handleGoToMyCourses : undefined}
        />
      )}

      {showSuccessPopup && !enrollResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-green-950/50 p-4 backdrop-blur-sm">
          <div className={`${cardTheme.shell} ${cardTheme.accentTop} w-full max-w-sm p-6 sm:p-8`}>
            <div className="mb-6 text-center">
              <div className={`mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl ${cardTheme.iconBoxGold}`}>
                <CheckCircle className="h-8 w-8" />
              </div>
              <h3 className={`mb-2 text-xl font-bold ${cardTheme.title}`}>تمام يا بطل!</h3>
              <p className={`text-sm ${cardTheme.subtitle}`}>مرحباً بيك في {courseDetails.courseName}</p>
            </div>
            <div className="space-y-3">
              <button type="button" onClick={handleStartCourse} className={`flex w-full min-h-11 items-center justify-center gap-2 rounded-xl py-3 ${cardTheme.btnPrimary}`}>
                <Play className="h-4 w-4" />
                يلا نبدأ الكورس
              </button>
              <button type="button" onClick={handleGoToMyCourses} className={`flex w-full min-h-11 items-center justify-center gap-2 rounded-xl py-3 ${cardTheme.btnOutline}`}>
                <BookOpen className="h-4 w-4" />
                كورساتي وحصصي
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mx-auto max-w-6xl px-3 py-4 sm:px-6 sm:py-8 lg:px-8 lg:py-10">
        {/* Hero — burgundy + gold */}
        <section className={`${cardTheme.shell} overflow-hidden`}>
          <div className={`relative ${cardTheme.headerBanner} px-4 py-6 sm:px-6 sm:py-8 lg:px-8 lg:py-10`}>
            <div className="pointer-events-none absolute -start-10 -top-10 h-40 w-40 rounded-full bg-gold-400/15 blur-2xl" />
            <div className="pointer-events-none absolute -end-8 bottom-0 h-36 w-36 rounded-full bg-white/10 blur-2xl" />

            <div className="relative z-10 grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(240px,320px)] lg:items-center lg:gap-8">
              <div className="order-2 min-w-0 text-center lg:order-1 lg:text-right">
                <div className="mb-3 flex flex-wrap items-center justify-center gap-2 lg:justify-start">
                  {gradeLabel && (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-gold-300/50 bg-gold-500/20 px-3 py-1 text-xs font-semibold text-gold-100 sm:text-sm">
                      <Sparkles className="h-3.5 w-3.5" />
                      {gradeLabel}
                    </span>
                  )}
                  {termLabel && (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold text-white sm:text-sm">
                      <CalendarDays className="h-3.5 w-3.5" />
                      {termLabel}
                    </span>
                  )}
                  {isFree && (
                    <span className="inline-flex items-center rounded-full bg-gold-500 px-3 py-1 text-xs font-bold text-green-950">
                      مجاني
                    </span>
                  )}
                </div>

                <h1 className="mb-3 text-xl font-bold leading-snug text-white sm:text-3xl lg:text-4xl font-cairo-bold">
                  {courseDetails.courseName}
                </h1>
                {courseDetails.description && (
                  <p className="mb-5 line-clamp-3 max-w-2xl text-sm leading-relaxed text-white/85 sm:text-base lg:mx-0 lg:line-clamp-4">
                    {courseDetails.description}
                  </p>
                )}

                <div className="mb-5 flex flex-wrap items-center justify-center gap-2 sm:gap-3 lg:justify-start">
                  <div className="inline-flex min-h-10 items-center gap-2 rounded-xl border border-white/15 bg-white/10 px-3 py-2">
                    <Video className="h-4 w-4 text-gold-300" />
                    <span className="text-sm font-semibold text-white">{totalLessons} درس</span>
                  </div>
                  <div className="inline-flex min-h-10 items-center gap-2 rounded-xl border border-gold-400/40 bg-gold-500/20 px-3 py-2">
                    <GraduationCap className="h-4 w-4 text-gold-300" />
                    <span className="text-sm font-bold text-gold-100">
                      {isFree ? 'مجاناً' : `${formattedPrice} جنيه`}
                    </span>
                  </div>
                </div>

                <div className="space-y-3">
                  {isCheckingEnrollment ? (
                    <div className="flex items-center justify-center gap-2 py-2 lg:justify-start">
                      <Loader2 className="h-5 w-5 animate-spin text-gold-300" />
                      <span className="text-sm text-white/80">جاري التحقق من التسجيل...</span>
                    </div>
                  ) : isEnrolled ? (
                    <button
                      type="button"
                      onClick={handleContinue}
                      className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-full bg-white px-6 py-2.5 text-sm font-semibold text-green-900 shadow-sm transition hover:bg-gold-50 sm:w-auto sm:text-base"
                    >
                      <Play className="h-4 w-4" />
                      متابعة الكورس
                    </button>
                  ) : !isAuthenticated ? (
                    <div className="flex flex-col gap-2.5 sm:flex-row sm:flex-wrap sm:justify-center lg:justify-start">
                      <button
                        type="button"
                        onClick={() =>
                          navigate('/login', {
                            state: {
                              redirectTo: isFree ? getCoursePath(courseDetails) : '/checkout',
                              course: {
                                id: courseDetails.id,
                                courseName: courseDetails.courseName,
                                description: courseDetails.description,
                                price: courseDetails.price,
                                imagePath: courseDetails.imagePath,
                                grade: courseDetails.grade,
                                term: courseDetails.term,
                              },
                              courseId: courseDetails.id,
                            },
                          })
                        }
                        className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-full bg-white px-6 py-2.5 text-sm font-semibold text-green-900 shadow-sm transition hover:bg-gold-50 sm:w-auto sm:min-w-[180px] sm:text-base"
                      >
                        <ShoppingCart className="h-4 w-4" />
                        سجّل الدخول للاشتراك
                      </button>
                    </div>
                  ) : isFree ? (
                    <button
                      type="button"
                      onClick={() => navigate(`/course-player/${id}`)}
                      className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-full bg-white px-6 py-2.5 text-sm font-semibold text-green-900 shadow-sm transition hover:bg-gold-50 sm:w-auto sm:text-base"
                    >
                      <Play className="h-4 w-4" />
                      ابدأ الكورس المجاني
                    </button>
                  ) : (
                    <div className="flex flex-col gap-2.5 sm:flex-row sm:flex-wrap sm:justify-center lg:justify-start">
                      <button
                        type="button"
                        onClick={handleCheckoutClick}
                        className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-full bg-gold-500 px-6 py-2.5 text-sm font-semibold text-green-950 shadow-sm transition hover:bg-gold-400 sm:w-auto sm:min-w-[160px] sm:text-base"
                      >
                        <ShoppingCart className="h-4 w-4" />
                        اشترك الآن
                      </button>
                    </div>
                  )}
                </div>

                {mounted && showCodeInput && !isFree && !isEnrolled && isAuthenticated && (
                  <div className="mt-5 rounded-2xl border border-white/15 bg-white/10 p-3.5 backdrop-blur-sm sm:mt-6 sm:p-5">
                    <h3 className="mb-3 text-sm font-bold text-white sm:text-base">لديك كود تسجيل؟</h3>
                    <div className="flex flex-col gap-2 sm:flex-row">
                      <input
                        type="text"
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="أدخل كود التسجيل"
                        className={`min-h-11 flex-1 rounded-xl border px-4 py-2.5 text-sm text-green-950 transition-colors focus:outline-none focus:ring-2 focus:ring-gold-400/50 ${
                          enrollmentError ? 'border-red-300 bg-red-50' : 'border-white/30 bg-white focus:border-gold-400'
                        }`}
                        disabled={isEnrolling}
                      />
                      <button
                        type="button"
                        onClick={handleCodeSubmit}
                        disabled={isEnrolling}
                        className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-gold-500 px-5 py-2.5 text-sm font-semibold text-green-950 transition hover:bg-gold-400 disabled:opacity-60"
                      >
                        {isEnrolling ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            جاري التسجيل...
                          </>
                        ) : (
                          <>
                            <CheckCircle className="h-4 w-4" />
                            تفعيل الكود
                          </>
                        )}
                      </button>
                    </div>
                    {enrollmentError && (
                      <p className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-center text-xs text-red-700">{enrollmentError}</p>
                    )}
                    {enrollmentSuccess && !enrollResult && (
                      <p className="mt-2 rounded-lg bg-green-50 px-3 py-2 text-center text-xs text-green-700">تمام! الكود اتفَعّل بنجاح</p>
                    )}
                  </div>
                )}
              </div>

              <div className="order-1 lg:order-2">
                <div className="relative overflow-hidden rounded-2xl border-2 border-gold-400/60 bg-green-950 shadow-lg shadow-black/20">
                  <div className="aspect-[16/10] w-full sm:aspect-[4/3] lg:aspect-[16/11]">
                    <img
                      src={getCourseImageSrc(courseDetails)}
                      alt={courseDetails.courseName}
                      className="h-full w-full object-cover"
                      onError={(e) => {
                        e.currentTarget.src = '/default-course.jpg';
                      }}
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-green-950/60 via-transparent to-transparent" />
                  <div className="absolute bottom-3 right-3 left-3">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-gold-500 px-3 py-1 text-xs font-bold text-green-950">
                      <Video className="h-3.5 w-3.5" />
                      {totalLessons} درس تعليمي
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Tabs + content */}
        <section className="mt-5 sm:mt-8">
          <div className="mb-4 flex justify-center sm:mb-5">
            <div className="inline-flex w-full max-w-md rounded-2xl border border-green-200/70 bg-white p-1 shadow-md shadow-green-900/10 sm:w-auto">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const active = selectedSection === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setSelectedSection(tab.id)}
                    className={`inline-flex min-h-11 flex-1 items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-xs font-semibold transition-all sm:flex-none sm:gap-2 sm:px-5 sm:text-sm ${
                      active
                        ? 'bg-green-800 text-white shadow-sm'
                        : 'text-green-800 hover:bg-green-50'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className={`${cardTheme.shell} ${cardTheme.accentTop} p-4 sm:p-6 lg:p-8`}>
            {selectedSection === 'curriculum' ? (
              <div>
                <div className="mb-5 text-center sm:mb-8 sm:text-right">
                  <span className={`mb-2 inline-flex ${cardTheme.badge}`}>
                    <BookOpen className="h-3.5 w-3.5" />
                    المنهج
                  </span>
                  <h2 className={`mb-1 text-lg font-bold sm:text-2xl ${cardTheme.title}`}>المنهج الدراسي</h2>
                  <p className={`text-sm sm:text-base ${cardTheme.subtitle}`}>
                    {totalLessons} درس منظم على {courseDetails.units.length} وحدة
                  </p>
                  <div className={`mx-auto mt-3 h-1 w-12 rounded-full sm:mx-0 ${cardTheme.divider}`} />
                </div>
                <Curriculum units={courseDetails.units} courseId={courseDetails.id} />
              </div>
            ) : (
              <div className="mx-auto max-w-4xl">
                <div className="mb-5 text-center sm:mb-8 sm:text-right">
                  <span className={`mb-2 inline-flex ${cardTheme.badgeGold}`}>
                    <Target className="h-3.5 w-3.5" />
                    نظرة عامة
                  </span>
                  <h2 className={`mb-1 text-lg font-bold sm:text-2xl ${cardTheme.title}`}>نظرة عامة</h2>
                  <p className={`text-sm sm:text-base ${cardTheme.subtitle}`}>تفاصيل الكورس وأهدافه التعليمية</p>
                  <div className={`mx-auto mt-3 h-1 w-12 rounded-full sm:mx-0 ${cardTheme.divider}`} />
                </div>

                <div className={`mb-4 ${cardTheme.feature} p-4 sm:mb-6 sm:p-6`}>
                  <h3 className={`mb-3 flex items-center gap-2 text-base font-bold sm:text-lg ${cardTheme.title}`}>
                    <Target className="h-5 w-5 text-gold-600" />
                    وصف الكورس
                  </h3>
                  <p className="text-sm leading-relaxed text-gray-700 sm:text-base">{courseDetails.description}</p>
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-5">
                  <div className={cardTheme.feature}>
                    <h3 className={`mb-4 flex items-center gap-2 text-base font-bold ${cardTheme.title}`}>
                      <GraduationCap className="h-5 w-5 text-green-700" />
                      تفاصيل الكورس
                    </h3>
                    <ul className="space-y-3 text-sm text-gray-700">
                      {gradeLabel && (
                        <li className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 shrink-0 text-gold-600" />
                          الصف: {gradeLabel}
                        </li>
                      )}
                      {termLabel && (
                        <li className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 shrink-0 text-gold-600" />
                          الفصل: {termLabel}
                        </li>
                      )}
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 shrink-0 text-gold-600" />
                        عدد الدروس: {totalLessons}
                      </li>
                    </ul>
                  </div>

                  <div className={cardTheme.feature}>
                    <h3 className={`mb-4 flex items-center gap-2 text-base font-bold ${cardTheme.title}`}>
                      <Sparkles className="h-5 w-5 text-gold-600" />
                      مميزات الكورس
                    </h3>
                    <ul className="space-y-3 text-sm text-gray-700">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 shrink-0 text-gold-600" />
                        شرح واضح يناسب طلاب كل المراحل
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 shrink-0 text-gold-600" />
                        تمارين ومراجعات شاملة
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 shrink-0 text-gold-600" />
                        متابعة من فريق الإشراف
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default CourseDetail;
